import { useState, useRef, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { 
  MessageSquare, Send, Loader2, Bot, User, X, ChevronDown, ChevronUp,
  Check, XCircle, FileText, Shield, AlertTriangle, Sparkles
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "./ui/collapsible";
import { toast } from "sonner";


// Simple diff visualization component
const DiffPreview = ({ original, updated, onAccept, onReject, language }) => {
  const t = language === 'cs' ? {
    changesPreview: "Náhled změn",
    acceptChanges: "Přijmout změny",
    rejectChanges: "Zamítnout",
    linesChanged: "řádků změněno",
    warning: "Zkontrolujte změny před přijetím"
  } : {
    changesPreview: "Changes Preview",
    acceptChanges: "Accept changes",
    rejectChanges: "Reject",
    linesChanged: "lines changed",
    warning: "Review changes before accepting"
  };

  // Simple line-by-line comparison
  const originalLines = (original || "").split('\n');
  const updatedLines = (updated || "").split('\n');
  
  // Count changed lines (simple heuristic)
  const changedCount = Math.abs(updatedLines.length - originalLines.length) + 
    Math.min(originalLines.length, updatedLines.length) - 
    originalLines.filter((line, i) => updatedLines[i] === line).length;

  return (
    <div className="border border-amber-300 rounded-lg bg-amber-50/50 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <span className="font-medium text-amber-800">{t.changesPreview}</span>
          <Badge variant="outline" className="bg-amber-100 text-amber-700">
            ~{changedCount} {t.linesChanged}
          </Badge>
        </div>
      </div>
      
      <p className="text-sm text-amber-700">{t.warning}</p>
      
      {/* Diff preview - scrollable */}
      <div className="max-h-[300px] overflow-y-auto border border-amber-200 rounded bg-white p-3">
        <pre className="text-xs whitespace-pre-wrap font-mono text-slate-700">
          {updated?.substring(0, 2000)}
          {updated?.length > 2000 && "\n\n... (truncated for preview)"}
        </pre>
      </div>
      
      {/* Action buttons */}
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onReject}
          className="gap-1 text-slate-600"
        >
          <XCircle className="h-4 w-4" />
          {t.rejectChanges}
        </Button>
        <Button
          size="sm"
          onClick={onAccept}
          className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          <Check className="h-4 w-4" />
          {t.acceptChanges}
        </Button>
      </div>
    </div>
  );
};

export const DocumentCopilotChat = ({
  documentId,
  documentContent,
  onDocumentUpdate,
  language = "en"
}) => {
  const { } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [applying, setApplying] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Translations
  const t = language === 'cs' ? {
    title: "AI Asistent dokumentu",
    subtitle: "Pracuje pouze s tímto dokumentem",
    placeholder: "Napište zprávu... (např. 'Přidej sekci o nočních operacích')",
    send: "Odeslat",
    sending: "Odesílání...",
    thinking: "AI přemýšlí...",
    emptyState: "Zeptejte se AI asistenta na úpravu nebo vysvětlení dokumentu.",
    examples: [
      "Přidej sekci o nočních operacích",
      "Uprav meteorologická minima pro urbánní prostředí",
      "Vysvětli požadavky na SAIL IV"
    ],
    scopeWarning: "AI má přístup pouze k tomuto dokumentu, misi a regulatorní databázi.",
    acceptingChanges: "Ukládání změn...",
    changesApplied: "Změny byly aplikovány",
    changesRejected: "Změny zamítnuty",
    errorSending: "Nepodařilo se odeslat zprávu",
    errorApplying: "Nepodařilo se aplikovat změny"
  } : {
    title: "AI Document Assistant",
    subtitle: "Works with this document only",
    placeholder: "Type a message... (e.g. 'Add a section about night operations')",
    send: "Send",
    sending: "Sending...",
    thinking: "AI is thinking...",
    emptyState: "Ask the AI assistant to edit or explain the document.",
    examples: [
      "Add a section about night operations",
      "Update meteorological minima for urban environment",
      "Explain SAIL IV requirements"
    ],
    scopeWarning: "AI has access only to this document, mission, and regulatory database.",
    acceptingChanges: "Applying changes...",
    changesApplied: "Changes applied",
    changesRejected: "Changes rejected",
    errorSending: "Failed to send message",
    errorApplying: "Failed to apply changes"
  };

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversationHistory]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Send message to AI
  const sendMessage = useCallback(async () => {
    if (!message.trim() || sending) return;

    const userMessage = message.trim();
    setMessage("");
    setSending(true);

    // Add user message to history
    const newHistory = [
      ...conversationHistory,
      { role: "user", content: userMessage }
    ];
    setConversationHistory(newHistory);

    try {
      const response = await api.post(
        `/compliance/documents/${documentId}/chat`,
        {
          message: userMessage,
          conversation_history: conversationHistory
        }
      );

      const { reply, updated_document } = response.data;

      // Add assistant reply to history
      setConversationHistory(prev => [
        ...prev,
        { role: "assistant", content: reply, hasUpdate: !!updated_document }
      ]);

      // If there's an updated document, show diff preview
      if (updated_document) {
        setPendingUpdate(updated_document);
      }

    } catch (error) {
      console.error("Chat error:", error);
      toast.error(t.errorSending);
      // Remove the user message on error
      setConversationHistory(conversationHistory);
    } finally {
      setSending(false);
    }
  }, [message, conversationHistory, documentId,  t.errorSending, sending]);

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Accept AI changes
  const acceptChanges = async () => {
    if (!pendingUpdate) return;

    setApplying(true);
    try {
      await api.post(
        `/compliance/documents/${documentId}/apply-changes`,
        { content: pendingUpdate }
      );

      toast.success(t.changesApplied);
      setPendingUpdate(null);
      
      // Notify parent to refresh document
      if (onDocumentUpdate) {
        onDocumentUpdate(pendingUpdate);
      }

    } catch (error) {
      console.error("Apply error:", error);
      toast.error(t.errorApplying);
    } finally {
      setApplying(false);
    }
  };

  // Reject AI changes
  const rejectChanges = () => {
    setPendingUpdate(null);
    toast.info(t.changesRejected);
  };

  // Set example prompt
  const handleExampleClick = (example) => {
    setMessage(example);
    inputRef.current?.focus();
  };

  return (
    <div className="mt-4" data-testid="document-copilot">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between gap-2 border-[#1E3A5F]/20 hover:border-[#1E3A5F]/40 hover:bg-[#1E3A5F]/5"
            data-testid="copilot-toggle-btn"
          >
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#1E3A5F]" />
              <span className="font-medium">{t.title}</span>
              <Badge variant="outline" className="text-xs bg-slate-100">
                {t.subtitle}
              </Badge>
            </div>
            {isOpen ? (
              <ChevronUp className="h-4 w-4 text-slate-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-slate-500" />
            )}
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <Card className="mt-2 border-[#1E3A5F]/20" data-testid="copilot-panel">
            <CardContent className="p-4 space-y-4">
              {/* Security notice */}
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded text-xs text-slate-600">
                <Shield className="h-3.5 w-3.5 text-slate-500" />
                {t.scopeWarning}
              </div>

              {/* Messages area */}
              <div className="min-h-[200px] max-h-[400px] overflow-y-auto space-y-3 p-2 border rounded-lg bg-white">
                {conversationHistory.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Bot className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm mb-4">{t.emptyState}</p>
                    <div className="space-y-2">
                      {t.examples.map((example, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleExampleClick(example)}
                          className="block w-full text-left text-xs px-3 py-2 bg-slate-50 hover:bg-slate-100 rounded border border-slate-200 text-slate-600 transition-colors"
                        >
                          "{example}"
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  conversationHistory.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      {msg.role === "assistant" && (
                        <div className="w-7 h-7 rounded-full bg-[#1E3A5F] flex items-center justify-center flex-shrink-0">
                          <Bot className="h-4 w-4 text-white" />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                          msg.role === "user"
                            ? "bg-[#1E3A5F] text-white"
                            : "bg-slate-100 text-slate-800"
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        {msg.hasUpdate && (
                          <div className="mt-2 pt-2 border-t border-slate-200/50">
                            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                              <FileText className="h-3 w-3 mr-1" />
                              {language === 'cs' ? 'Navrženy změny dokumentu' : 'Document changes suggested'}
                            </Badge>
                          </div>
                        )}
                      </div>
                      {msg.role === "user" && (
                        <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                          <User className="h-4 w-4 text-slate-600" />
                        </div>
                      )}
                    </div>
                  ))
                )}

                {/* Sending indicator */}
                {sending && (
                  <div className="flex gap-2">
                    <div className="w-7 h-7 rounded-full bg-[#1E3A5F] flex items-center justify-center flex-shrink-0">
                      <Bot className="h-4 w-4 text-white" />
                    </div>
                    <div className="bg-slate-100 rounded-lg px-3 py-2 text-sm text-slate-500 flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t.thinking}
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Pending update diff preview */}
              {pendingUpdate && (
                <DiffPreview
                  original={documentContent}
                  updated={pendingUpdate}
                  onAccept={acceptChanges}
                  onReject={rejectChanges}
                  language={language}
                />
              )}

              {/* Applying overlay */}
              {applying && (
                <div className="flex items-center justify-center gap-2 p-3 bg-emerald-50 rounded-lg text-emerald-700">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t.acceptingChanges}
                </div>
              )}

              {/* Input area */}
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t.placeholder}
                  disabled={sending || applying}
                  className="flex-1"
                  data-testid="copilot-input"
                />
                <Button
                  onClick={sendMessage}
                  disabled={!message.trim() || sending || applying}
                  className="gap-2 bg-[#1E3A5F] hover:bg-[#1E3A5F]/90"
                  data-testid="copilot-send-btn"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  <span className="hidden sm:inline">{sending ? t.sending : t.send}</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default DocumentCopilotChat;
